import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { 
  Image, 
  Upload, 
  RefreshCw, 
  Search,
  X,
  Trash2,
  Copy,
  Check,
  FileImage,
  Calendar,
  HardDrive,
  Download,
  Eye,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getUploadedImages, uploadImage, deleteUploadedImage } from '../../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Upload constraints
const UPLOAD_CONSTRAINTS = {
  maxSizeKB: 120,
  maxSizeBytes: 120 * 1024,
  allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  allowedExtensions: '.jpg, .jpeg, .png, .gif, .webp',
  maxDimension: 1200, // Max width or height in pixels
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('sv-SE', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Resize and compress image to target size
const resizeAndCompressImage = (file, maxSizeKB = 300, maxDimension = 1600) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        // Scale down if larger than max dimension
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        // High-quality bilinear smoothing instead of the browser default
        // (produces noticeably crisper result for photographs / book covers
        // when rendered at smaller sizes later).
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to get under target size
        const tryCompress = (quality) => {
          return new Promise((res) => {
            canvas.toBlob((blob) => {
              res(blob);
            }, 'image/jpeg', quality);
          });
        };
        
        // Start with high quality and reduce until under limit (floor at 0.6
        // so we never produce heavily JPEG-artifacted output — if we still
        // can't hit the size target we fall through to the dimension-shrink
        // pass below instead of continuing to lose image quality).
        const compress = async () => {
          let quality = 0.92;
          let blob = await tryCompress(quality);
          
          while (blob && blob.size > maxSizeKB * 1024 && quality > 0.6) {
            quality -= 0.08;
            blob = await tryCompress(quality);
          }
          
          // If still too large, reduce dimensions further
          if (blob && blob.size > maxSizeKB * 1024) {
            const smallerCanvas = document.createElement('canvas');
            const scale = Math.sqrt((maxSizeKB * 1024) / blob.size) * 0.9;
            smallerCanvas.width = Math.round(width * scale);
            smallerCanvas.height = Math.round(height * scale);
            const smallCtx = smallerCanvas.getContext('2d');
            smallCtx.imageSmoothingEnabled = true;
            smallCtx.imageSmoothingQuality = 'high';
            smallCtx.drawImage(img, 0, 0, smallerCanvas.width, smallerCanvas.height);
            
            blob = await new Promise((res) => {
              smallerCanvas.toBlob((b) => res(b), 'image/jpeg', 0.85);
            });
          }
          
          if (blob) {
            // Create new file with original name but .jpg extension
            const newFileName = file.name.replace(/\.[^.]+$/, '.jpg');
            const compressedFile = new File([blob], newFileName, { type: 'image/jpeg' });
            resolve({
              file: compressedFile,
              originalSize: file.size,
              compressedSize: blob.size,
              width: canvas.width,
              height: canvas.height
            });
          } else {
            reject(new Error('Failed to compress image'));
          }
        };
        
        compress();
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const MediaLibrary = ({ isSelector = false, onSelect = null }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [images, setImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUploadedImages();
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Kunde inte ladda bildbiblioteket');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Filter images based on search
  const filteredImages = images.filter(img => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      img.original_filename?.toLowerCase().includes(query) ||
      img.filename?.toLowerCase().includes(query) ||
      img.content_type?.toLowerCase().includes(query)
    );
  });

  // Process and upload files with compression
  const processAndUploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    // Filter for valid image types
    const imageFiles = Array.from(files).filter(file => 
      UPLOAD_CONSTRAINTS.allowedTypes.includes(file.type) || 
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      toast.error('Endast bildfiler är tillåtna: ' + UPLOAD_CONSTRAINTS.allowedExtensions);
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      setUploadProgress(`Bearbetar ${i + 1}/${imageFiles.length}: ${file.name}`);
      
      try {
        let fileToUpload = file;
        
        // Compress if file is larger than target
        if (file.size > UPLOAD_CONSTRAINTS.maxSizeBytes) {
          setUploadProgress(`Komprimerar ${file.name} (${formatFileSize(file.size)})...`);
          const result = await resizeAndCompressImage(
            file, 
            UPLOAD_CONSTRAINTS.maxSizeKB,
            UPLOAD_CONSTRAINTS.maxDimension
          );
          fileToUpload = result.file;
          console.log(`Compressed ${file.name}: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.compressedSize)}`);
        }
        
        setUploadProgress(`Laddar upp ${fileToUpload.name}...`);
        await uploadImage(fileToUpload);
        successCount++;
      } catch (error) {
        console.error('Error processing/uploading file:', error);
        errorCount++;
        toast.error(`Kunde inte ladda upp ${file.name}`);
      }
    }

    setUploading(false);
    setUploadProgress('');
    
    if (successCount > 0) {
      toast.success(`${successCount} bild(er) uppladdade och optimerade`);
      fetchImages();
    }
    if (errorCount > 0 && successCount === 0) {
      toast.error(`Kunde inte ladda upp ${errorCount} bild(er)`);
    }
  };

  // Handle file upload
  const handleUpload = async (e) => {
    await processAndUploadFiles(e.target.files);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle image deletion
  const handleDelete = async (image) => {
    if (!window.confirm(`Vill du verkligen ta bort "${image.original_filename}"?`)) {
      return;
    }

    try {
      await deleteUploadedImage(image.id);
      toast.success('Bild borttagen');
      fetchImages();
      setSelectedImage(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Kunde inte ta bort bilden');
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (image) => {
    const fullUrl = `${API_URL}${image.url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(image.id);
      toast.success('URL kopierad!');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Kunde inte kopiera URL');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    await processAndUploadFiles(e.dataTransfer.files);
  };

  // Handle image selection for picker mode
  const handleImageSelect = (image) => {
    if (isSelector && onSelect) {
      onSelect(`${API_URL}${image.url}`);
    } else {
      setSelectedImage(selectedImage?.id === image.id ? null : image);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div 
      className={`space-y-6 ${isSelector ? 'p-0' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
            <Upload className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Släpp bilder här</h3>
            <p className="text-gray-600">Bilder komprimeras automatiskt till &lt;120KB</p>
          </div>
        </div>
      )}

      {/* Header - hide in selector mode */}
      {!isSelector && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bildbibliotek</h2>
            <p className="text-gray-600">Hantera och återanvänd uppladdade bilder ({images.length} bilder)</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchImages}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Uppdatera
            </Button>
            <label className="cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept={UPLOAD_CONSTRAINTS.allowedExtensions}
                multiple
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
              />
              <Button
                asChild
                className="bg-green-600 hover:bg-green-700"
                disabled={uploading}
              >
                <span>
                  {uploading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Ladda upp
                </span>
              </Button>
            </label>
          </div>
        </div>
      )}

      {/* Upload Constraints Info */}
      {!isSelector && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Uppladdningsformat</p>
              <ul className="space-y-1 text-blue-700">
                <li>• <strong>Format:</strong> {UPLOAD_CONSTRAINTS.allowedExtensions}</li>
                <li>• <strong>Storlek:</strong> Bilder komprimeras automatiskt till &lt;{UPLOAD_CONSTRAINTS.maxSizeKB}KB</li>
                <li>• <strong>Dimensioner:</strong> Max {UPLOAD_CONSTRAINTS.maxDimension}×{UPLOAD_CONSTRAINTS.maxDimension}px (skalas automatiskt)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Compact header for selector mode */}
      {isSelector && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4 pb-2 border-b">
            <p className="text-sm text-gray-600">{images.length} bilder</p>
            <label className="cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept={UPLOAD_CONSTRAINTS.allowedExtensions}
                multiple
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
              />
              <Button size="sm" variant="outline" asChild disabled={uploading}>
                <span>
                  {uploading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  <span className="ml-1">Ladda upp</span>
                </span>
              </Button>
            </label>
          </div>
          <p className="text-xs text-gray-500">
            {UPLOAD_CONSTRAINTS.allowedExtensions} • Auto-komprimeras till &lt;{UPLOAD_CONSTRAINTS.maxSizeKB}KB
          </p>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && uploadProgress && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
          <RefreshCw className="w-4 h-4 text-amber-600 animate-spin flex-shrink-0" />
          <p className="text-sm text-amber-800">{uploadProgress}</p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Sök bilder..."
          className="pl-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Image Grid - 1:1 aspect ratio */}
      {filteredImages.length === 0 ? (
        <Card className="p-12 text-center">
          <FileImage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">
            {searchQuery ? 'Inga bilder matchade sökningen' : 'Inga bilder uppladdade'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery ? 'Försök med en annan sökterm' : 'Ladda upp bilder för att börja bygga ditt bibliotek'}
          </p>
        </Card>
      ) : (
        <div className={`grid gap-4 ${isSelector ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'}`}>
          {filteredImages.map((image) => (
            <Card 
              key={image.id}
              className={`group cursor-pointer overflow-hidden transition-all hover:shadow-lg ${
                selectedImage?.id === image.id ? 'ring-2 ring-blue-500' : ''
              } ${isSelector ? 'hover:ring-2 hover:ring-green-500' : ''}`}
              onClick={() => handleImageSelect(image)}
            >
              {/* 1:1 Aspect Ratio Container */}
              <div className="aspect-square relative bg-gray-100">
                <img
                  src={`${API_URL}${image.url}`}
                  alt={image.original_filename}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {isSelector ? (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-1" />
                      Välj
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewImage(image);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(image);
                        }}
                      >
                        {copiedUrl === image.id ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <CardContent className="p-2">
                <p className="text-xs font-medium text-gray-700 truncate" title={image.original_filename}>
                  {image.original_filename}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(image.size)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Selected Image Details Panel - hide in selector mode */}
      {selectedImage && !isSelector && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-blue-800">
                <Image className="w-5 h-5" />
                Bilddetaljer
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Preview - 1:1 aspect ratio */}
              <div className="w-full md:w-48 flex-shrink-0">
                <div className="aspect-square relative bg-gray-100 rounded-lg overflow-hidden border">
                  <img
                    src={`${API_URL}${selectedImage.url}`}
                    alt={selectedImage.original_filename}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Details */}
              <div className="flex-1 space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Filnamn</label>
                  <p className="text-sm font-medium text-gray-900">{selectedImage.original_filename}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                      <HardDrive className="w-3 h-3" /> Storlek
                    </label>
                    <p className="text-sm text-gray-700">{formatFileSize(selectedImage.size)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Typ</label>
                    <p className="text-sm text-gray-700">{selectedImage.content_type}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Uppladdad
                  </label>
                  <p className="text-sm text-gray-700">{formatDate(selectedImage.uploaded_at)}</p>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">URL</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={`${API_URL}${selectedImage.url}`}
                      readOnly
                      className="text-xs font-mono bg-white"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(selectedImage)}
                    >
                      {copiedUrl === selectedImage.id ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 pt-2">
                  <a
                    href={`${API_URL}${selectedImage.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border rounded-md text-sm hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    Öppna
                  </a>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(selectedImage)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Ta bort
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl" aria-describedby="preview-description">
          <DialogHeader>
            <DialogTitle>{previewImage?.original_filename}</DialogTitle>
          </DialogHeader>
          <p id="preview-description" className="sr-only">Förhandsgranskning av bild</p>
          {previewImage && (
            <div className="relative">
              <img
                src={`${API_URL}${previewImage.url}`}
                alt={previewImage.original_filename}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {formatFileSize(previewImage.size)} • {previewImage.content_type}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(previewImage)}
                  >
                    {copiedUrl === previewImage.id ? (
                      <>
                        <Check className="w-4 h-4 mr-1 text-green-600" />
                        Kopierad!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Kopiera URL
                      </>
                    )}
                  </Button>
                  <a
                    href={`${API_URL}${previewImage.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md text-sm hover:bg-gray-200"
                  >
                    <Download className="w-4 h-4" />
                    Öppna i ny flik
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Usage Instructions - hide in selector mode */}
      {!isSelector && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Image className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Hur du använder bildbiblioteket</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Dra och släpp bilder direkt på sidan för att ladda upp</li>
                  <li>• Stora bilder komprimeras automatiskt till &lt;{UPLOAD_CONSTRAINTS.maxSizeKB}KB</li>
                  <li>• Klicka på en bild för att se detaljer och kopiera URL</li>
                  <li>• Använd URL:en i nyheter, artiklar eller andra innehållsfält</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MediaLibrary;
